import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { ID, database } from "../../../../appWrite"; // Adjust based on your Appwrite setup
import { fetchFAQs } from "../../../../redux/slices/FaqSlice";
import { useAppDispatch } from "../../../../redux/hooks";

interface FAQFormProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FAQData {
  question: string;
  answer: string;
}

export default function FAQModal({ isOpen, onClose }: FAQFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FAQData>();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const onSubmit = async (data: FAQData) => {
    setIsLoading(true);
    try {
      await database.createDocument(
        "67ce8a4a0032e13c6368",
        "67d7ec6f0022005176aa",
        ID.unique(),
        data
      );
      reset();
      onClose();
      alert("FAQ added successfully!");
      dispatch(fetchFAQs());
      setIsLoading(false);
    } catch (error) {
      console.error("Error adding FAQ:", error);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <DialogTitle className="text-lg font-semibold mb-4">
                Add FAQ
              </DialogTitle>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Question
                  </label>
                  <input
                    {...register("question", {
                      required: "Question is required",
                    })}
                    className="mt-1 p-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter question"
                  />
                  {errors.question && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.question.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Answer
                  </label>
                  <textarea
                    {...register("answer", { required: "Answer is required" })}
                    className="mt-1 p-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter answer"
                  />
                  {errors.answer && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.answer.message}
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`px-4 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 ${
                      isLoading && "animate-pulse"
                    }`}
                  >
                    {isLoading ? "Creating..." : "Add FAQ"}
                  </button>
                </div>
              </form>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
